import { getAttribution } from './attribution';
import { getApiUrl } from '../config/api';

/**
 * Tags third-party forms on the same page as the chat widget with a hidden
 * attribution reference, the same way apps/form-api/src/standalone.ts does for
 * chatbot-less sites.
 *
 * Why this exists: the widget's own captureAttribution() only ever reaches the
 * CRM through the widget's own lead-creation request, which already attaches
 * `attribution` directly — no reference needed. But a page can ALSO have its own
 * WordPress/Elementor/Gravity Forms form that has nothing to do with the widget.
 * That form posts wherever its own plugin sends it, and previously nothing on
 * the page told it what the visitor's origin was. This closes that gap for any
 * site that already has the chat widget installed, without requiring a second
 * script tag.
 *
 * Deliberately kept out of attribution.ts: that file is an identical copy in
 * three bundles (widget, widget_v2, form-api) with no imports of its own. This
 * needs the widget's bot_id and its own API base, neither of which the other two
 * bundles have a use for.
 */

const REF_FIELD = 'zentri_ref';
const REF_MARKER = 'data-zentri-ref';
const SKIP_ATTR = 'data-zentri-skip';
const TOKEN_ENDPOINT = '/api/submit-lead/attribution/token';
const CACHE_KEY = 'zentri:attr:ref';

/**
 * Elementor only relays fields saved in the form's own configuration, so a
 * field this script creates itself is dropped before it reaches the webhook.
 * When a site has added one through Elementor's Form Fields editor instead,
 * its Custom ID may not be typed exactly `zentri_ref` — matching loosely
 * tolerates casing and separator differences. Scoped to Elementor's own
 * `form_fields[...]` naming so this can never pick up an unrelated field of
 * the site's own that happens to contain "zentri".
 */
const BUILDER_FIELD_PREFIX = 'input[name^="form_fields["]';
const BUILDER_FIELD_NAME_PATTERN = /zentri[-_ ]?ref/i;
const ELEMENTOR_FORM_CLASS = 'elementor-form';

function findBuilderField(form: HTMLFormElement): HTMLInputElement | null {
    const candidates = form.querySelectorAll<HTMLInputElement>(BUILDER_FIELD_PREFIX);
    for (const input of candidates) {
        if (input.hasAttribute(REF_MARKER)) continue;
        if (BUILDER_FIELD_NAME_PATTERN.test(input.name)) return input;
    }
    return null;
}

interface CachedRef {
    fp: string;
    token: string;
}

let currentToken: string | null = null;
let minting = false;

function fingerprint(value: unknown): string {
    const text = JSON.stringify(value);
    let hash = 0;
    for (let i = 0; i < text.length; i += 1) {
        hash = (hash * 31 + text.charCodeAt(i)) | 0;
    }
    return String(hash);
}

function readCache(): CachedRef | null {
    try {
        const raw = window.sessionStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as CachedRef;
        return parsed?.fp && parsed?.token ? parsed : null;
    } catch {
        return null;
    }
}

function writeCache(entry: CachedRef): void {
    try {
        window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(entry));
    } catch {
        // Storage full or blocked — the token still works for this page load.
    }
}

/** GET forms are excluded on purpose — see standalone.ts's copy of this note. */
function eligibleForms(): HTMLFormElement[] {
    const forms = Array.from(document.forms || []);
    return forms.filter((form) => {
        if (form.hasAttribute(SKIP_ATTR)) return false;
        const method = (form.getAttribute('method') || 'get').toLowerCase();
        return method === 'post';
    });
}

function tagForm(form: HTMLFormElement, token: string): void {
    try {
        const builderField = findBuilderField(form);
        if (builderField) {
            builderField.value = token;
            return;
        }

        const owned = form.querySelectorAll<HTMLInputElement>(`input[${REF_MARKER}]`);
        if (owned.length) {
            owned.forEach((input) => { input.value = token; });
            return;
        }

        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = REF_FIELD;
        input.value = token;
        input.setAttribute(REF_MARKER, '');
        form.appendChild(input);

        if (form.classList.contains(ELEMENTOR_FORM_CLASS)) {
            tagElementorShapedField(form, token);
        }
    } catch {
        // Form in a shadow root or otherwise unwritable — skip it.
    }
}

/**
 * A field built with Elementor's own markup and `form_fields[...]` naming, for
 * a form that has not had the real field added yet. Elementor's relay is keyed
 * off its saved field list rather than what shows up in the DOM, so this is a
 * best-effort stand-in — the field configured through the editor is what
 * actually reaches the webhook reliably.
 */
function tagElementorShapedField(form: HTMLFormElement, token: string): void {
    const wrapper = document.createElement('div');
    wrapper.className = `elementor-field-type-hidden elementor-field-group elementor-column elementor-field-group-${REF_FIELD} elementor-col-100`;

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = `form_fields[${REF_FIELD}]`;
    input.className = 'elementor-field elementor-size-sm elementor-field-textual';
    input.value = token;
    input.setAttribute(REF_MARKER, '');

    wrapper.appendChild(input);
    form.appendChild(wrapper);
}

function tagAllForms(): void {
    if (!currentToken) return;
    for (const form of eligibleForms()) tagForm(form, currentToken);
}

async function ensureToken(botId: string): Promise<void> {
    if (minting) return;

    const payload = getAttribution();
    if (!payload) return;

    const fp = fingerprint(payload);

    const cached = readCache();
    if (cached && cached.fp === fp) {
        currentToken = cached.token;
        tagAllForms();
        return;
    }

    minting = true;
    try {
        const response = await fetch(getApiUrl(TOKEN_ENDPOINT), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ botId, attribution: payload }),
            credentials: 'omit',
            keepalive: true
        });

        if (!response.ok) return;

        const json = (await response.json()) as { token?: string | null };
        if (!json?.token) return;

        currentToken = json.token;
        writeCache({ fp, token: json.token });
        tagAllForms();
    } catch {
        // Network failure, blocked by an extension, offline — the widget's own
        // leads are unaffected; only third-party forms on the page miss out.
    } finally {
        minting = false;
    }
}

function watchForForms(botId: string): void {
    try {
        if (typeof MutationObserver === 'undefined') return;

        const observer = new MutationObserver(() => {
            if (!eligibleForms().length) return;
            if (currentToken) tagAllForms();
            else void ensureToken(botId);
        });

        observer.observe(document.documentElement, { childList: true, subtree: true });
    } catch {
        // No observer: forms present at start are still tagged.
    }
}

function tagOnSubmit(): void {
    try {
        document.addEventListener(
            'submit',
            (event) => {
                const form = event.target as HTMLFormElement | null;
                if (!currentToken || !form || form.tagName !== 'FORM') return;
                if (form.hasAttribute(SKIP_ATTR)) return;
                if ((form.getAttribute('method') || 'get').toLowerCase() !== 'post') return;
                tagForm(form, currentToken);
            },
            true
        );
    } catch {
        // ignore
    }
}

/**
 * Start watching for and tagging third-party forms on this page.
 *
 * Call once the widget knows its bot_id. Does not call captureAttribution() —
 * the widget already does that at module load, before bot_id is even known, so
 * the origin is recorded regardless of whether this ever finds a form to tag.
 */
export function startFormTagging(botId: string): void {
    if (!botId) return;
    watchForForms(botId);
    tagOnSubmit();
    if (eligibleForms().length) void ensureToken(botId);
}
